import {connect} from 'react-redux';
import {type ActionCreatorsMapObject, bindActionCreators, type Dispatch} from 'redux';

import {createSelector} from 'reselect';

import {type GlobalState} from 'types/store/index';

import {type Post} from 'mattermost-redux/types/posts';

import {type FileInfo} from 'mattermost-redux/types/files';

import {type ActionResult} from 'mattermost-redux/types/actions';

import {type CommandArgs} from 'mattermost-redux/types/integrations';

import {type ModalData} from 'types/actions';

import {getConfig, getLicense} from 'mattermost-redux/selectors/entities/general';
import {getCurrentTeam, getCurrentTeamId, getTeam} from 'mattermost-redux/selectors/entities/teams';

import {getCurrentChannel, getCurrentChannelStats, getChannel} from 'mattermost-redux/selectors/entities/channels';
import {getCurrentUser, getCurrentUserId, getStatusForUserId, getUser, makeGetProfilesInChannel} from 'mattermost-redux/selectors/entities/users';
import {haveIChannelPermission} from 'mattermost-redux/selectors/entities/roles';
import {getChannelTimezones} from 'mattermost-redux/actions/channels';
import {
    getBool,
    isCustomGroupsEnabled,
    getTutorialStep,
    getChannelDisplayMode,
    getTeammateNameDisplaySetting,
} from 'mattermost-redux/selectors/entities/preferences';
import {type PreferenceType} from 'mattermost-redux/types/preferences';
import {savePreferences} from 'mattermost-redux/actions/preferences';
import {
    getLatestReplyablePostId,
    makeGetMessageInHistoryItem,
    getPost,
    makeGetPostIdsForThread,
    makeGetPostsForThread,
} from 'mattermost-redux/selectors/entities/posts';
import {getAssociatedGroupsForReferenceByMention} from 'mattermost-redux/selectors/entities/groups';
import {
    addMessageIntoHistory,
    moveHistoryIndexBack,
    moveHistoryIndexForward,
    removeReaction,
} from 'mattermost-redux/actions/posts';
import {Permissions, Posts, Preferences as PreferencesRedux} from 'mattermost-redux/constants';

import {connectionErrorCount} from 'selectors/views/system';

import {addReaction, setEditingPost, emitShortcutReactToLastPostFrom, unsetEditingPost, createPost} from 'actions/post_actions';
import {editPost} from 'actions/views/posts';
import {scrollPostListToBottom} from 'actions/views/channel';
import {selectPostFromRightHandSideSearchByPostId} from 'actions/views/rhs';
import {setShowPreviewOnCreatePost} from 'actions/views/textbox';
import {executeCommand} from 'actions/command';
import {runMessageWillBePostedHooks, runSlashCommandWillBePostedHooks} from 'actions/hooks';
import {getIsRhsExpanded} from 'selectors/rhs';
import {showPreviewOnCreateComment} from 'selectors/views/textbox';
import {getCurrentLocale} from 'selectors/i18n';
import {getEmojiMap, getShortcutReactToLastPostEmittedFrom} from 'selectors/emojis';
import {openModal} from 'actions/views/modals';
import {Constants, Preferences, UserStatuses} from 'utils/constants';
import {canUploadFiles} from 'utils/file_utils';
import {OnboardingTourSteps} from 'components/onboarding_tour';

import {getSiteURL} from 'utils/url';
import {isFeatureEnabled} from 'utils/utils';

import {setDraftAction} from 'features/drafts';

import {getEditingPost} from 'selectors/posts';

import {isPostPendingOrFailed} from 'mattermost-redux/utils/post_utils';
import {getUserIdFromChannelName} from 'mattermost-redux/utils/channel_utils';
import {type UserProfile} from 'mattermost-redux/types/users';

import {displayUsername} from 'mattermost-redux/utils/user_utils';

import {WithCommentDraft} from './with_comment_draft';

interface OwnProps {
    rootId: string;
    channelId: string;
    latestPostId: string;
    teammate?: UserProfile;
}

function makeMapStateToProps() {
    const getCurrentUsersLatestPost = makeGetCurrentUsersLatestReply();
    const getPostsForThread = makeGetPostsForThread();
    const getMessageInHistoryItem = makeGetMessageInHistoryItem(Posts.MESSAGE_TYPES.POST as any);
    const doGetProfilesInChannel = makeGetProfilesInChannel();

    return (state: GlobalState, {channelId: propsCurrentChannelId, rootId, teammate}: OwnProps) => {
        const config = getConfig(state);
        const license = getLicense(state);
        const stateCurrentChannel = getCurrentChannel(state) || {};
        const currentChannel = getChannel(state, propsCurrentChannelId) || stateCurrentChannel;
        const currentChannelTeammateUsername = getUser(state, currentChannel.teammate_id || '')?.username;
        const latestReplyablePostId = getLatestReplyablePostId(state);
        const currentChannelMembersCount = getCurrentChannelStats(state) ? getCurrentChannelStats(state).member_count : 1;
        const enableEmojiPicker = config.EnableEmojiPicker === 'true';
        const enableConfirmNotificationsToChannel = config.EnableConfirmNotificationsToChannel === 'true';
        const currentUserId = getCurrentUserId(state);
        const currentUserName = getCurrentUser(state)?.username;
        const userIsOutOfOffice = getStatusForUserId(state, currentUserId) === UserStatuses.OUT_OF_OFFICE;
        const badConnection = connectionErrorCount(state) > 1;
        const isTimezoneEnabled = config.ExperimentalTimezone === 'true';
        const shortcutReactToLastPostEmittedFrom = getShortcutReactToLastPostEmittedFrom(state);
        const canPost = haveIChannelPermission(state, currentChannel?.team_id, currentChannel?.id, Permissions.CREATE_POST_IN_THREAD);
        const useChannelMentions = haveIChannelPermission(state, currentChannel?.team_id, currentChannel?.id, Permissions.USE_CHANNEL_MENTIONS);
        const isLDAPEnabled = license?.IsLicensed === 'true' && license?.LDAPGroups === 'true';
        const useGroupMentions = haveIChannelPermission(state, currentChannel?.team_id, currentChannel?.id, Permissions.USE_GROUP_MENTIONS);
        const useCustomGroupMentions = useGroupMentions && isCustomGroupsEnabled(state);
        const useLDAPGroupMentions = useGroupMentions && isLDAPEnabled;
        const currentTeamId = getCurrentTeamId(state);
        const groupsWithAllowReference = useLDAPGroupMentions || useCustomGroupMentions ? getAssociatedGroupsForReferenceByMention(state, currentTeamId, currentChannel.id) : null;
        const enableTutorial = config.EnableTutorial === 'true';
        const tutorialStep = getTutorialStep(state);
        const showSendTutorialTip = enableTutorial && tutorialStep === OnboardingTourSteps.SEND_MESSAGE;
        const editingPost = getEditingPost(state);
        const isAuthor = editingPost?.post?.user_id === currentUserId;
        const deletePermission = isAuthor ? Permissions.DELETE_POST : Permissions.DELETE_OTHERS_POSTS;
        const editPermission = isAuthor ? Permissions.EDIT_POST : Permissions.EDIT_OTHERS_POSTS;

        const postsInThread = getPostsForThread(state, rootId);
        const currentChannelLatestPost = postsInThread[0];

        const currentTeam = getCurrentTeam(state);
        const team = getTeam(state, currentChannel?.team_id) || currentTeam;
        const teamUrl = `${getSiteURL()}/${team?.name || currentTeam.name}`;

        const newEditorEnabled = isFeatureEnabled(Constants.PRE_RELEASE_FEATURES.NEW_EDITOR, state);
        const pluginMenuItems = newEditorEnabled && state.plugins.components.CreatePostDropdownMenuV2.length ? (
            state.plugins.components.CreatePostDropdownMenuV2
        ) : (
            state.plugins.components.CreatePostDropdownMenu
        );

        let currentChannelDisplayName = currentChannel?.display_name || teammate?.username;

        const temmateNameDisplaySetting = getTeammateNameDisplaySetting(state);
        if (currentChannel.type === Constants.DM_CHANNEL) {
            const dmUserId = getUserIdFromChannelName(currentUserId, currentChannel?.name);
            const dmUser = getUser(state, dmUserId);
            currentChannelDisplayName = displayUsername(dmUser, temmateNameDisplaySetting);
        } else if (currentChannel.type === Constants.GM_CHANNEL) {
            const gmMembers = doGetProfilesInChannel(state, currentChannel.id);
            currentChannelDisplayName = gmMembers
                .filter(({id}) => id !== currentUserId)
                .map((gmMember) => displayUsername(gmMember, temmateNameDisplaySetting))
                .join(', ');
        }

        return {
            currentTeamId,
            currentChannelTeammateUsername,
            currentChannelMembersCount,
            currentUserId,
            codeBlockOnCtrlEnter: getBool(state, PreferencesRedux.CATEGORY_ADVANCED_SETTINGS, 'code_block_ctrl_enter', true),
            canEditPost: haveIChannelPermission(state, currentChannel?.team_id, currentChannel?.id, editPermission),
            canDeletePost: haveIChannelPermission(state, currentChannel?.team_id, currentChannel?.id, deletePermission),
            ctrlSend: getBool(state, Preferences.CATEGORY_ADVANCED_SETTINGS, 'send_on_ctrl_enter'),
            fullWidthTextBox: getChannelDisplayMode(state) === Preferences.CHANNEL_DISPLAY_MODE_FULL_SCREEN,
            showSendTutorialTip,
            messageInHistoryItem: getMessageInHistoryItem(state),
            latestReplyablePostId,
            locale: getCurrentLocale(state),
            currentUsersLatestPost: getCurrentUsersLatestPost(state, rootId),
            currentChannelLatestPost,
            canUploadFiles: canUploadFiles(config),
            enableEmojiPicker,
            enableConfirmNotificationsToChannel,
            maxPostSize: parseInt(config.MaxPostSize || '', 10) || Constants.DEFAULT_CHARACTER_LIMIT,
            userIsOutOfOffice,
            rhsExpanded: getIsRhsExpanded(state),
            emojiMap: getEmojiMap(state),
            badConnection,
            isTimezoneEnabled,
            shortcutReactToLastPostEmittedFrom,
            canPost,
            useChannelMentions,
            shouldShowPreview: showPreviewOnCreateComment(state),
            groupsWithAllowReference,
            useLDAPGroupMentions,
            isLDAPEnabled,
            useCustomGroupMentions,
            pluginMenuItems,
            teamName: team.name,
            teamUrl,
            rootId,
            isThread: true,
            currentChannelId: currentChannel?.id,
            currentChannelDisplayName,
            currentChannelType: currentChannel?.type,
            currentUserName,
            editingPost,
            isEditing: editingPost.show && editingPost.isRHS,
        };
    };
}

function makeGetCurrentUsersLatestReply() {
    const getPostIdsInThread = makeGetPostIdsForThread();
    return createSelector(
        getCurrentUserId,
        getPostIdsInThread,
        (state) => (id: string) => getPost(state, id),
        (_state, rootId) => rootId,
        (userId, postIds, getPostById, rootId) => {
            let lastPost = null;

            if (!postIds) {
                return lastPost;
            }

            for (const id of postIds) {
                const post = getPostById(id) || {};

                // don't edit webhook posts, deleted posts, or system messages
                if (
                    post.user_id !== userId ||
                    (post.props && post.props.from_webhook) ||
                    post.state === Constants.POST_DELETED ||
                    (post.type && post.type.startsWith(Constants.SYSTEM_MESSAGE_PREFIX)) ||
                    isPostPendingOrFailed(post)
                ) {
                    continue;
                }

                if (rootId) {
                    if (post.root_id === rootId || post.id === rootId) {
                        lastPost = post;
                        break;
                    }
                } else {
                    lastPost = post;
                    break;
                }
            }

            return lastPost;
        },
    );
}

type Actions = {
    setShowPreview: (showPreview: boolean) => void;
    addMessageIntoHistory: (message: string) => void;
    moveHistoryIndexBack: (index: string) => Promise<void>;
    moveHistoryIndexForward: (index: string) => Promise<void>;
    addReaction: (postId: string, emojiName: string) => void;
    onSubmitPost: (post: Post, fileInfos: FileInfo[]) => void;
    removeReaction: (postId: string, emojiName: string) => void;

    // clearDraftUploads: () => void;
    runMessageWillBePostedHooks: (originalPost: Post) => ActionResult;
    runSlashCommandWillBePostedHooks: (originalMessage: string, originalArgs: CommandArgs) => ActionResult;
    setDraft: typeof setDraftAction;
    setEditingPost: (postId?: string, refocusId?: string, title?: string, isRHS?: boolean) => void;
    selectPostFromRightHandSideSearchByPostId: (postId: string) => void;
    openModal: <P>(modalData: ModalData<P>) => void;
    closeModal: (modalId: string) => void;
    executeCommand: (message: string, args: CommandArgs) => ActionResult;
    getChannelTimezones: (channelId: string) => ActionResult;
    scrollPostListToBottom: () => void;
    emitShortcutReactToLastPostFrom: (emittedFrom: string) => void;
    savePreferences: (userId: string, preferences: PreferenceType[]) => ActionResult;
    openEditChannelHeaderModal: () => void;
    openEditChannelPurposeModal: () => void;
    unsetEditingPost: () => void;
    editPost: (input: Partial<Post>) => Promise<Post>;
}

function onSubmitPost(post: Post, fileInfos: FileInfo[]) {
    return (dispatch: Dispatch) => dispatch(createPost(post, fileInfos));
}

function mapDispatchToProps() {
    return (dispatch: Dispatch) => ({
        actions: bindActionCreators<ActionCreatorsMapObject<any>, Actions>({
            addMessageIntoHistory,
            onSubmitPost,
            moveHistoryIndexBack,
            moveHistoryIndexForward,
            addReaction,
            removeReaction,
            setDraft: setDraftAction,
            selectPostFromRightHandSideSearchByPostId,
            setEditingPost,
            emitShortcutReactToLastPostFrom,
            openModal,
            executeCommand,
            getChannelTimezones,
            runMessageWillBePostedHooks,
            runSlashCommandWillBePostedHooks,
            scrollPostListToBottom,
            setShowPreview: setShowPreviewOnCreatePost,
            savePreferences,
            unsetEditingPost,
            editPost,
        }, dispatch),
    });
}

export const AdvancedEditorCreateComment = connect(makeMapStateToProps, mapDispatchToProps, null, {forwardRef: true})(WithCommentDraft);
